import { useCallback, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";

import {
  Member,
  MemberWithPermission,
  RoleDefinitions,
  SelectablePermission,
  Team,
  TeamWithPermission,
} from "../../types";
import { EDITOR_ITEM } from "../../constants";
import useFocusSearchInput from "../../hooks/useFocusSearchInput";
import { useDebouncedSearchTerm } from "../../hooks/useDebouncedSearchTerm";
import { RoleDropdown } from "../RoleDropdown";

import styles from "./SearchInput.module.css";

type Props = {
  hasEditorAndViewerRoles: boolean;
  hasMultipleTeams: boolean;
  isDisabled: boolean;
  isBusy: boolean;
  isBusyAdding: boolean;
  onShare: (item: MemberWithPermission | TeamWithPermission) => Promise<void>;
  searchResults: (Member | Team)[];
  onInputValueChange: (searchTerm: string) => void;
  roleDefinitions: RoleDefinitions;
  /** Host dialog element for Select/Menu portals when nested in ModalDialog. */
  portalContainer?: Element;
};

export default function SearchInput({
  hasMultipleTeams,
  hasEditorAndViewerRoles,
  onShare,
  searchResults,
  isDisabled,
  isBusyAdding,
  onInputValueChange,
  roleDefinitions,
  portalContainer,
}: Props) {
  const initialSelectedItem = EDITOR_ITEM;

  const [selectedItem, setSelectedItem] = useState<Member | Team | null>(null);

  const [selectedRole, setSelectedRole] = useState<SelectablePermission>(
    initialSelectedItem.value,
  );

  const getLabelForItem = useCallback((memberOrTeam: Member | Team) => {
    const isPerson = "full_name" in memberOrTeam;
    if (isPerson) {
      return memberOrTeam.full_name;
    }

    return `${memberOrTeam.name}, ${memberOrTeam.member_count} members`;
  }, []);

  const typeaheadRef = useRef<HTMLDivElement>(null);
  useFocusSearchInput({ isDisabled, typeaheadRef });

  const { inputValue, handleInputChange, setInputValue } =
    useDebouncedSearchTerm(onInputValueChange);

  const clearSelection = useCallback(() => {
    setSelectedItem(null);
    setInputValue("");
  }, [setInputValue]);

  const onInputChange = useCallback(
    (value: string) => {
      handleInputChange(value);
      // Match controlled Typeahead pattern: empty input clears selection
      // (including the clear/revert control when it empties the field).
      if (value === "") {
        setSelectedItem(null);
      }
    },
    [handleInputChange],
  );

  const handleSelectionChange = useCallback(
    (key: string | number | null) => {
      if (key === null) {
        setSelectedItem(null);
        setInputValue("");
        return;
      }

      const found =
        searchResults.find((r) => String(r.id) === String(key)) ?? null;
      setSelectedItem(found);
      // Controlled inputValue is not updated by Typeahead on select — sync the label.
      if (found) {
        setInputValue(getLabelForItem(found));
      }
    },
    [getLabelForItem, searchResults, setInputValue],
  );

  return (
    <>
      <div className={styles.searchInputWrapper}>
        <div className={styles.searchInputCol1}>
          <div ref={typeaheadRef}>
            <Typeahead
              label={hasMultipleTeams ? "Add member or team" : "Add member"}
              placeholder=""
              selectedKey={selectedItem ? String(selectedItem.id) : null}
              onSelectionChange={handleSelectionChange}
              inputValue={inputValue}
              onInputChange={onInputChange}
              isDisabled={isDisabled}
            >
              {searchResults.map((item) => (
                <TypeaheadItem
                  key={String(item.id)}
                  id={String(item.id)}
                  textValue={getLabelForItem(item)}
                >
                  <Avatar
                    name={(item as Member).full_name || (item as Team).name}
                    url={(item as Member).photo_url || (item as Team).logo}
                    size="small"
                  />
                  {getLabelForItem(item)}
                </TypeaheadItem>
              ))}
            </Typeahead>
          </div>
        </div>
        <RoleDropdown
          initialSelectedItem={initialSelectedItem}
          hasEditorAndViewerRoles={hasEditorAndViewerRoles}
          roleDefinitions={roleDefinitions}
          onChange={setSelectedRole}
          portalContainer={portalContainer}
        />
        <div className={styles.searchInputCol3}>
          <Button
            isDisabled={!selectedItem || isDisabled}
            isPending={isBusyAdding}
            onPress={async () => {
              if (selectedItem) {
                await onShare({
                  ...selectedItem,
                  permission: selectedRole,
                });

                clearSelection();
              }
            }}
          >
            Share
          </Button>
        </div>
      </div>
    </>
  );
}
